import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Network } from "vis-network/standalone";
import "vis-network/styles/vis-network.css";
import { eventService } from "../../utils/services/eventService";
import CustomTypography from "../../globalStyles/fonts";
import { toast } from "react-toastify";
import {
  Box,
  IconButton,
  CircularProgress,
  useTheme,
  Button,
} from "@mui/material";
import DashboardContainer from "../../components/dashboard-container";
import JsonDataRenderer from "../../components/json-data-renderer";
import EditableHierarchicalJsonViewer from "../../components/json-data-render-modal";
const OrderWorkFlow = () => {
  const networkContainer = useRef(null);
  const { id } = useParams();
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNodeData, setSelectedNodeData] = useState(null);
  const [selectedNodeStatus, setSelectedNodeStatus] = useState(null);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [expandView, setExpandView] = useState(false);
  const [loading, setLoading] = useState(false);
  const theme = useTheme();
  function formatString(str) {
    return str
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }
  const options = {
    nodes: {
      shape: "box",
      font: { size: 16, color: "#FFFFFF" },
      margin: 10,
    },
    edges: {
      arrows: { to: { enabled: true, scaleFactor: 1.5 } },
      smooth: { type: "continuous" },
    },
    physics: { enabled: false },
    interaction: {
      hover: true,
      dragNodes: true,
      dragView: true,
      zoomView: true,
    },
    layout: {
      hierarchical: {
        direction: "UD",
        sortMethod: "directed",
        nodeSpacing: 200,
        levelSeparation: 150,
        treeSpacing: 300,
        blockShifting: true,
        edgeMinimization: true,
        parentCentralization: true,
      },
    },
  };

  const fetchEventLogs = async () => {
    setSelectedNodeData(null);
    setLoading(true);
    try {
      const response = await eventService.getEventLogs(id);

      const processedIds = new Set();
      const nodeList = response.data.map((event, index) => {
        const { step, event_name, status, parent, ...rest } = event;
        let uniqueId = step;
        let counter = 1;
        while (processedIds.has(uniqueId)) {
          uniqueId = `${step}_${counter}`;
          counter++;
        }

        processedIds.add(uniqueId);

        return {
          id: uniqueId, // Use the uniqueId here
          label: formatString(event_name),
          color: status === "Success" ? "#018749" : "#FF6347",
          message: rest,
          status,
          originalStep: step,
          parent: parent,
          status: status,
          child: rest.child,
        };
      });

      const sortedNodeList = nodeList.sort(
        (a, b) => a.originalStep - b.originalStep,
      );
      if (sortedNodeList.find((x) => x.parent == "0")) {
        function buildStepFlow(events) {
          const steps = [];
          const stepMap = {};
          events.forEach((event) => {
            stepMap[event.step] = event;
          });
          const root = events.find((e) => e.parent == "0");

          function addEdges(from) {
            const children = events.filter(
              (e) =>
                e.parent == from.originalStep || from.child === e.originalStep,
            );
            for (const child of children) {
              steps.push({ from: from.id, to: child.id });
              addEdges(child);
            }
          }

          addEdges(root);

          return steps;
        }
        setEdges(buildStepFlow(sortedNodeList));
      } else {
        const edgeList = [];

        for (let i = 0; i < sortedNodeList.length - 1; i++) {
          const fromNode = sortedNodeList[i];
          const toNode = sortedNodeList[i + 1];
          edgeList.push({
            from: fromNode.id,
            to: toNode.id,
          });
        }
        setEdges(edgeList);
      }
      setNodes(sortedNodeList);
    } catch (error) {
      console.error("Error fetching event logs:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchEventLogs();
    }
  }, [id]);

  useEffect(() => {
    if (nodes.length && edges.length) {
      const data = { nodes, edges };
      const network = new Network(networkContainer.current, data, options);

      network.on("click", function (event) {
        const clickedNodeId = event.nodes[0];
        if (clickedNodeId) {
          const clickedNode = nodes.find((node) => node.id === clickedNodeId);

          if (clickedNode) {
            setSelectedNodeId(clickedNode.id);
            setSelectedNodeData(clickedNode);
            setSelectedNodeStatus(clickedNode.status);
          }
        }
      });
    }
  }, [nodes, edges]);

  const handleRetrigger = async (data) => {
    if (!selectedNodeId || !selectedNodeData) {
      toast.error(
        "No node selected for re-trigger. Please choose any of the node to re-trigger.",
      );
    }
    setLoading(true);
    try {
      const response = await eventService.retrigger(
        data
          ? {
              id: selectedNodeData.message._id,
              input: data.input,
              extra_data: data.extra_data,
            }
          : { id: selectedNodeData.message._id },
      );
      fetchEventLogs();
      // alert(`Event with ID ${selectedNodeId} retriggered successfully.`);
      // Optionally refresh the event logs after retriggering
      // fetchEventLogs();
    } catch (error) {
      console.error(
        `Failed to retrigger event with ID ${selectedNodeId}:`,
        error,
      );
      // toast.error("Failed to retrigger event. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0, 0, 0, 0.5)",
        zIndex: 9999,
        color: "white",
      }}
    >
      <CircularProgress color="inherit" />
    </Box>
  ) : (
    <DashboardContainer
      workflow
      onClickRefresh={fetchEventLogs}
      headerButtons={
        selectedNodeData ? (
          <div style={{ display: "flex", flexDirection: "row" }}>
            <div>
              {selectedNodeData ? (
                <Button
                  size="small"
                  style={{
                    height: 30,
                    textTransform: "none",
                    fontSize: 14,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  variant="contained"
                  onClick={() => setExpandView(true)}
                  color="primary"
                >
                  Edit Event Data
                </Button>
              ) : (
                <></>
              )}
            </div>
            <Button
              size="small"
              style={{
                height: 30,
                textTransform: "none",
                fontSize: 14,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                marginLeft: 5,
              }}
              variant="contained"
              onClick={handleRetrigger}
              color="primary"
            >
              Retrigger Event
            </Button>
          </div>
        ) : (
          <></>
        )
      }
      hideUploadButton
      hideAddButton
      hideGridList
      header={"Work flow"}
    >
      <div style={{ display: "flex", height: "78vh" }}>
        <div
          style={{
            height: "90%",
            width: "70%",
            border: "1px dotted",
            marginTop: 15,
          }}
          ref={networkContainer}
        ></div>
        <div
          style={{
            width: "30%",
            borderRight: "1px solid var(--border-color)",
            borderTop: "1px solid var(--border-color)",
            marginTop: 15,
            padding: "10px",
          }}
        >
          <div
            style={{
              overflow: "auto",
              height: "90%",
            }}
          >
            {selectedNodeData ? (
              <div>
                <JsonDataRenderer
                  jsonData={selectedNodeData}
                ></JsonDataRenderer>
                {/* <JsonForm renderData={selectedNodeData.message}></JsonForm> */}
              </div>
            ) : (
              <CustomTypography style={{ fontWeight: "500" }}>
                Click on a node to see its details
              </CustomTypography>
            )}
          </div>
        </div>
        <EditableHierarchicalJsonViewer
          onSaveWorkFlowData={(data) => handleRetrigger({ input: data })}
          data={selectedNodeData?.message?.input}
          onClose={() => setExpandView(false)}
          open={expandView}
          step={selectedNodeData?.label}
        ></EditableHierarchicalJsonViewer>
      </div>
    </DashboardContainer>
  );
};

export default OrderWorkFlow;
